import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import { useNavigate } from 'react-router-dom'
import PastBookDetail from './PastBookDetail'
import AddBook from './AddBook'
import ToReadList from './ToReadList'
import CurrentlyReadingDetail from './CurrentlyReadingDetail'
import EditProfile from './EditProfile'
import RecommendationChat from './RecommendationChat'
import Footer from './Footer'
import InviteFriends from './InviteFriends'
import { formatDate } from '../utils/dateFormat'

interface DashboardProps {
  user: {
    id: number
    name: string
    email: string
    preferred_affiliate_provider?: string | null
  }
  onSignOut: () => void
  onClubClick: (clubId: number) => void
  onCreateClub: () => void
}

interface Club {
  id: number
  name: string
  created_at: string
  member_count: number
}

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface BookPick {
  id: number
  club_id?: number
  club_name: string
  status: string
  started_at?: string
  completed_at?: string
  book: Book
  picked_by?: {
    id: number
    name: string
    email: string
  }
  season?: {
    id: number
    season_number: number
  }
  average_rating?: number
  review_count: number
}

interface CurrentlyReadingBook {
  id: number
  club_id?: number
  club_name: string
  status: string
  started_at?: string
  book: Book
  picked_by?: {
    id: number
    name: string
  }
  user_progress?: {
    id: number
    status: string
    progress_percentage?: number
  }
}

export default function Dashboard({ user: initialUser, onSignOut, onClubClick, onCreateClub }: DashboardProps) {
  const navigate = useNavigate()
  const [user, setUser] = useState(initialUser)
  const [clubs, setClubs] = useState<Club[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [pastReads, setPastReads] = useState<BookPick[]>([])
  const [currentlyReading, setCurrentlyReading] = useState<CurrentlyReadingBook[]>([])
  const [selectedPastBook, setSelectedPastBook] = useState<BookPick | null>(null)
  const [showPastBookDetail, setShowPastBookDetail] = useState(false)
  const [showAddBook, setShowAddBook] = useState(false)
  const [selectedCurrentlyReading, setSelectedCurrentlyReading] = useState<CurrentlyReadingBook | null>(null)
  const [showCurrentlyReadingDetail, setShowCurrentlyReadingDetail] = useState(false)
  const [showEditProfile, setShowEditProfile] = useState(false)
  const [showRecommendationChat, setShowRecommendationChat] = useState(false)
  const [toReadRefreshTrigger, setToReadRefreshTrigger] = useState(0)
  const [showInviteFriends, setShowInviteFriends] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [clubsResponse, booksResponse, currentlyReadingResponse] = await Promise.all([
        fetch('/api/v1/clubs'),
        fetch('/api/v1/my_book_picks'),
        fetch('/api/v1/currently_reading')
      ])

      if (clubsResponse.ok) {
        const data = await clubsResponse.json()
        setClubs(data.clubs)
      } else {
        setError('Failed to load clubs')
      }

      if (booksResponse.ok) {
        const data = await booksResponse.json()
        setPastReads(data.book_picks)
      }

      if (currentlyReadingResponse.ok) {
        const data = await currentlyReadingResponse.json()
        setCurrentlyReading(data.currently_reading)
      }
    } catch (err) {
      setError('Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const handleProviderSelected = async (provider: string) => {
    // Update local state immediately for responsiveness
    setUser({ ...user, preferred_affiliate_provider: provider })

    // Save to backend
    try {
      await fetchWithCsrf('/api/v1/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user: {
            preferred_affiliate_provider: provider
          }
        })
      })
    } catch (err) {
      console.error('Failed to save affiliate provider preference:', err)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
                BooksForUs
              </span>
            </div>
            <button
              onClick={onSignOut}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors px-4 py-2 rounded-lg hover:bg-gray-100"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Section */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
                  <span className="text-2xl font-bold text-indigo-600">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">{user.name}</h2>
                <p className="text-gray-600 text-sm mb-6">{user.email}</p>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center py-3 border-t border-gray-100">
                    <span className="text-gray-600 text-sm">Clubs</span>
                    <span className="font-semibold text-indigo-600">{clubs.length}</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-t border-gray-100">
                    <span className="text-gray-600 text-sm">Books Read</span>
                    <span className="font-semibold text-gray-900">{pastReads.length}</span>
                  </div>
                </div>

                <button
                  onClick={() => setShowRecommendationChat(true)}
                  className="w-full py-2.5 px-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-medium rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all text-sm mb-3"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                    Get Recommendations
                  </span>
                </button>
                <button
                  onClick={() => navigate('/import')}
                  className="w-full py-2.5 px-4 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium rounded-lg transition-colors text-sm mb-3"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Import from Goodreads
                  </span>
                </button>
                <button
                  onClick={() => setShowInviteFriends(true)}
                  className="w-full py-2.5 px-4 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium rounded-lg transition-colors text-sm mb-3"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    Invite Friends
                  </span>
                </button>
                <button
                  onClick={() => setShowAddBook(true)}
                  className="w-full py-2.5 px-4 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium rounded-lg transition-colors text-sm mb-3"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Book
                  </span>
                </button>
                <button
                  onClick={() => navigate('/my-stats')}
                  className="w-full py-2.5 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium rounded-lg transition-colors text-sm mb-3"
                >
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    My Stats
                  </span>
                </button>
                <button
                  onClick={() => setShowEditProfile(true)}
                  className="w-full py-2.5 px-4 bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium rounded-lg transition-colors text-sm mb-3"
                >
                  Edit Profile
                </button>
                {user.user_type === 'admin' && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="w-full py-2.5 px-4 bg-purple-50 hover:bg-purple-100 text-purple-700 font-medium rounded-lg transition-colors text-sm"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Admin Dashboard
                    </span>
                  </button>
                )}
              </div>
            </div>

            {/* Clubs Card */}
            {!loading && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Your Clubs</h3>
                  <button
                    onClick={onCreateClub}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    title="New Club"
                  >
                    + New
                  </button>
                </div>
                {clubs.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {clubs.map((club) => (
                      <button
                        key={club.id}
                        onClick={() => onClubClick(club.id)}
                        className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-200 hover:border-indigo-300 group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-900 truncate group-hover:text-indigo-600 mb-1">
                              {club.name}
                            </h4>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>{club.member_count} {club.member_count === 1 ? 'member' : 'members'}</span>
                              <span>•</span>
                              <span>Created {formatDate(club.created_at)}</span>
                            </div>
                          </div>
                          <svg
                            className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 flex-shrink-0 ml-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={onCreateClub}
                    className="w-full py-3 px-4 border-2 border-dashed border-gray-300 text-gray-600 font-medium rounded-lg hover:border-indigo-500 hover:text-indigo-600 transition-all text-sm"
                  >
                    + Create Your First Club
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Currently Reading Section */}
            {!loading && (
              <div className="mb-8">
                <div className="mb-6">
                  <h2 className="text-3xl font-bold text-gray-900">Currently Reading</h2>
                </div>

                {currentlyReading.length > 0 ? (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 divide-y divide-gray-200">
                    {currentlyReading.map(reading => (
                      <button
                        key={reading.id}
                        onClick={() => {
                          setSelectedCurrentlyReading(reading)
                          setShowCurrentlyReadingDetail(true)
                        }}
                        className="w-full p-4 hover:bg-gray-50 transition-colors text-left group"
                      >
                        <div className="flex gap-4">
                          {reading.book.cover_url ? (
                            <img
                              src={reading.book.cover_url}
                              alt={reading.book.title}
                              className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors">
                              {reading.book.title}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">by {reading.book.author}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>{reading.club_name}</span>
                              {reading.started_at && (
                                <>
                                  <span>•</span>
                                  <span>Started {formatDate(reading.started_at)}</span>
                                </>
                              )}
                            </div>
                          </div>
                          {/* Circular Progress */}
                          <div className="relative w-16 h-16 flex-shrink-0">
                            <svg className="w-16 h-16 transform -rotate-90">
                              {/* Background circle */}
                              <circle
                                cx="32"
                                cy="32"
                                r="28"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                                className="text-gray-200"
                              />
                              {/* Progress circle */}
                              <circle
                                cx="32"
                                cy="32"
                                r="28"
                                stroke="currentColor"
                                strokeWidth="4"
                                fill="none"
                                strokeDasharray={`${2 * Math.PI * 28}`}
                                strokeDashoffset={`${2 * Math.PI * 28 * (1 - (reading.user_progress?.progress_percentage || 0) / 100)}`}
                                className="text-indigo-600 transition-all duration-300"
                                strokeLinecap="round"
                              />
                            </svg>
                            {/* Percentage text */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-xs font-semibold text-gray-700">
                                {Math.round(reading.user_progress?.progress_percentage || 0)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
                      <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No books currently reading</h3>
                    <p className="text-gray-600 mb-6">Start reading a book from your list or add a new one!</p>
                    <button
                      onClick={() => setShowAddBook(true)}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add a Book
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* To Read List */}
            {!loading && (
              <div className="mt-8">
                <ToReadList
                  user={user}
                  onBookStarted={fetchData}
                  refreshTrigger={toReadRefreshTrigger}
                  limit={5}
                  showSeeAll={true}
                  onSeeAll={() => navigate('/my-to-read')}
                  onProviderSelected={handleProviderSelected}
                />
              </div>
            )}

            {/* Past Reads Section */}
            {!loading && (
              <div className="mt-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">My Past Reads</h2>
                  {pastReads.length > 0 && (
                    <button
                      onClick={() => navigate('/my-past-reads')}
                      className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
                    >
                      See All
                    </button>
                  )}
                </div>
                {pastReads.length > 0 ? (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 divide-y divide-gray-200">
                    {pastReads.slice(0, 10).map(pick => (
                    <div
                      key={pick.id}
                      className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedPastBook(pick)
                        setShowPastBookDetail(true)
                      }}
                    >
                      <div className="flex flex-col sm:flex-row gap-4">
                        {pick.book.cover_url ? (
                          <img
                            src={pick.book.cover_url}
                            alt={pick.book.title}
                            className="w-12 h-18 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-18 bg-gradient-to-br from-gray-100 to-gray-200 rounded flex items-center justify-center">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{pick.book.title}</h3>
                          <p className="text-sm text-gray-600">by {pick.book.author}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <span>{pick.club_name}</span>
                            {pick.completed_at && (
                              <>
                                <span>•</span>
                                <span>Completed {formatDate(pick.completed_at)}</span>
                              </>
                            )}
                            {pick.average_rating && (
                              <>
                                <span>•</span>
                                <div className="flex items-center gap-1">
                                  <svg className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                  </svg>
                                  <span>{pick.average_rating}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
                      <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No books yet</h3>
                    <p className="text-gray-600 mb-6">Start tracking the books you've read with your clubs!</p>
                    <button
                      onClick={() => setShowAddBook(true)}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Your First Book
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Past Book Detail Modal */}
      {showPastBookDetail && selectedPastBook && (
        <PastBookDetail
          bookPick={selectedPastBook}
          clubId={selectedPastBook.club_id}
          userProvider={user.preferred_affiliate_provider}
          onProviderSelected={handleProviderSelected}
          onClose={() => {
            setShowPastBookDetail(false)
            setSelectedPastBook(null)
          }}
          onDelete={() => {
            fetchData()
          }}
        />
      )}

      {/* Add Book Modal */}
      {showAddBook && (
        <AddBook
          onClose={() => setShowAddBook(false)}
          onSuccess={() => {
            setShowAddBook(false)
            fetchData()
            setToReadRefreshTrigger(prev => prev + 1)
          }}
          userProvider={user.preferred_affiliate_provider}
          onProviderSelected={handleProviderSelected}
        />
      )}

      {/* Currently Reading Detail Modal */}
      {showCurrentlyReadingDetail && selectedCurrentlyReading && (
        <CurrentlyReadingDetail
          reading={selectedCurrentlyReading}
          userProvider={user.preferred_affiliate_provider}
          onProviderSelected={handleProviderSelected}
          onClose={() => {
            setShowCurrentlyReadingDetail(false)
            setSelectedCurrentlyReading(null)
          }}
          onComplete={() => {
            setShowCurrentlyReadingDetail(false)
            setSelectedCurrentlyReading(null)
            fetchData()
          }}
          onDelete={() => {
            fetchData()
            setToReadRefreshTrigger(prev => prev + 1)
          }}
        />
      )}

      {/* Edit Profile Modal */}
      {showEditProfile && (
        <EditProfile
          user={user}
          onClose={() => setShowEditProfile(false)}
          onSuccess={(updatedUser) => {
            setUser(updatedUser)
            setShowEditProfile(false)
          }}
        />
      )}

      {/* Invite Friends Modal */}
      {showInviteFriends && (
        <InviteFriends
          onClose={() => setShowInviteFriends(false)}
        />
      )}

      {/* Personal Recommendation Chat Modal */}
      {showRecommendationChat && (
        <RecommendationChat
          mode="personal"
          userName={user.name}
          onClose={() => setShowRecommendationChat(false)}
          onBookChosen={() => {
            fetchData()
          }}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  )
}
